import React, { useEffect, useState, useMemo } from 'react';
import DashboardLayout from '../../../layouts/DashboardLayout';
import api from '../../../api';
import { Users, UserPlus, Trash2, AlertCircle, X, ChevronDown, Search } from 'lucide-react';

function Avatar({ name, colorClass = 'bg-primary-100 text-primary-700' }) {
    return (
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${colorClass}`}>
            {name?.charAt(0).toUpperCase() || '?'}
        </div>
    );
}

function AssignModal({ open, onClose, supervisors, clinicians, onAssigned }) {
    const [selSup, setSelSup] = useState('');
    const [selRep, setSelRep] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const reset = () => { setSelSup(''); setSelRep(''); setError(null); };

    const handleClose = () => { reset(); onClose(); };

    const handleSubmit = async () => {
        if (!selSup || !selRep) { setError('Select both a supervisor and a clinician.'); return; }
        setSaving(true);
        setError(null);
        try {
            await api.assignReportee(parseInt(selSup), parseInt(selRep));
            reset();
            onAssigned();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to assign.');
        } finally {
            setSaving(false);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleClose} />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
                <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                            <UserPlus className="w-4 h-4 text-primary-600" />
                        </div>
                        <h2 className="text-base font-bold text-secondary-900">Assign Clinician</h2>
                    </div>
                    <button onClick={handleClose} className="text-secondary-400 hover:text-secondary-600 transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {error && (
                    <div className="flex items-center gap-2 bg-red-50 border border-red-100 text-red-700 rounded-lg px-3 py-2.5 text-sm mb-4">
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />{error}
                    </div>
                )}

                <div className="space-y-4">
                    <div>
                        <label className="block text-xs font-semibold text-secondary-500 mb-1.5">Supervisor</label>
                        <div className="relative">
                            <select
                                value={selSup}
                                onChange={e => setSelSup(e.target.value)}
                                className="w-full appearance-none border border-secondary-200 rounded-lg px-3 py-2.5 text-sm text-secondary-800 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 pr-8"
                            >
                                <option value="">Select supervisor...</option>
                                {supervisors.map(s => (
                                    <option key={s.id} value={s.id}>{s.name} ({s.email})</option>
                                ))}
                            </select>
                            <ChevronDown className="absolute right-2.5 top-3 w-4 h-4 text-secondary-400 pointer-events-none" />
                        </div>
                        {supervisors.length === 0 && (
                            <p className="text-xs text-amber-600 mt-1">No one to choose from yet. Add a doctor or supervisor user first.</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-secondary-500 mb-1.5">Junior Clinician</label>
                        <div className="relative">
                            <select
                                value={selRep}
                                onChange={e => setSelRep(e.target.value)}
                                className="w-full appearance-none border border-secondary-200 rounded-lg px-3 py-2.5 text-sm text-secondary-800 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 pr-8"
                            >
                                <option value="">Select clinician...</option>
                                {clinicians.filter(c => String(c.id) !== String(selSup)).map(c => (
                                    <option key={c.id} value={c.id}>{c.name} ({c.email})</option>
                                ))}
                            </select>
                            <ChevronDown className="absolute right-2.5 top-3 w-4 h-4 text-secondary-400 pointer-events-none" />
                        </div>
                    </div>
                </div>

                <div className="flex gap-3 mt-6">
                    <button
                        onClick={handleClose}
                        className="flex-1 px-4 py-2.5 border border-secondary-200 text-secondary-600 rounded-lg text-sm font-semibold hover:bg-secondary-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={saving || !selSup || !selRep}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold transition-colors"
                    >
                        <UserPlus className="w-4 h-4" />
                        {saving ? 'Assigning...' : 'Assign'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function ManageSupervisors() {
    const [mappings, setMappings] = useState([]);
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [clinicians, setClinicians] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);

    const [filterSupervisor, setFilterSupervisor] = useState('');
    const [filterReportee, setFilterReportee] = useState('');

    useEffect(() => { loadAll(); }, []);

    const loadAll = async () => {
        setLoading(true);
        try {
            const [mappingsRes, usersRes, rolesRes, doctorsRes] = await Promise.all([
                api.getAllSupervisorMappings(),
                api.fetchUsers(),
                api.fetchRoles(),
                api.getDoctorsList(),
            ]);
            setMappings(mappingsRes.data);
            setUsers(usersRes.data.data ? usersRes.data.data : (usersRes.data || []));
            setRoles(rolesRes.data);
            setClinicians(doctorsRes.data.doctors || []);
        } catch {
            setError('Failed to load data.');
        } finally {
            setLoading(false);
        }
    };

    const roleIdFor = (name) => roles.find(r => r.name.toLowerCase() === name)?.id;
    // Anyone senior can supervise: dedicated Supervisor logins, or any doctor (who keeps their own role).
    // Doctors aren't in /users or the visible roles list, so they come from the doctors list.
    const supervisors = useMemo(() => {
        const supervisorRoleId = roleIdFor('supervisor');
        const byId = new Map();
        users.filter(u => u.is_active && u.role_id === supervisorRoleId).forEach(u => byId.set(u.id, { id: u.id, name: u.name, email: u.email }));
        clinicians.forEach(c => { if (!byId.has(c.id)) byId.set(c.id, { id: c.id, name: c.name, email: c.email }); });
        return [...byId.values()].sort((x, y) => (x.name || '').localeCompare(y.name || ''));
    }, [users, roles, clinicians]); // eslint-disable-line

    // Flatten mappings into rows: { mappingId, supervisor, reportee, assignedAt }
    const rows = useMemo(() => {
        const flat = [];
        mappings.forEach(m => {
            if (m.supervisor && m.reportee) {
                flat.push({
                    supervisorId: m.supervisor.id,
                    reporteeId: m.reportee.id,
                    supervisorName: m.supervisor.name || '',
                    supervisorEmail: m.supervisor.email || '',
                    reporteeName: m.reportee.name || '',
                    reporteeEmail: m.reportee.email || '',
                    assignedAt: m.assigned_at,
                });
            }
        });
        return flat;
    }, [mappings]);

    const filtered = useMemo(() => {
        const supQ = filterSupervisor.toLowerCase();
        const repQ = filterReportee.toLowerCase();
        return rows.filter(r =>
            (!supQ || r.supervisorName.toLowerCase().includes(supQ) || r.supervisorEmail.toLowerCase().includes(supQ)) &&
            (!repQ || r.reporteeName.toLowerCase().includes(repQ) || r.reporteeEmail.toLowerCase().includes(repQ))
        );
    }, [rows, filterSupervisor, filterReportee]);

    const [expandedMobileSups, setExpandedMobileSups] = useState({});

    const toggleMobileSup = (supId) => {
        setExpandedMobileSups(prev => ({ ...prev, [supId]: !prev[supId] }));
    };

    const groupedBySupMobile = useMemo(() => {
        const groups = {};
        filtered.forEach(r => {
            if (!groups[r.supervisorId]) {
                groups[r.supervisorId] = {
                    supervisorId: r.supervisorId,
                    supervisorName: r.supervisorName,
                    supervisorEmail: r.supervisorEmail,
                    reportees: []
                };
            }
            groups[r.supervisorId].reportees.push(r);
        });
        return Object.values(groups);
    }, [filtered]);

    const handleUnassign = async (supervisorId, reporteeId) => {
        if (!window.confirm('Remove this clinician from the supervisor?')) return;
        try {
            await api.unassignReportee(supervisorId, reporteeId);
            await loadAll();
        } catch {
            setError('Failed to remove assignment.');
        }
    };

    const handleAssigned = async () => {
        setModalOpen(false);
        await loadAll();
    };

    return (
        <DashboardLayout>
            <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">

                {/* Header */}
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-heading font-bold text-secondary-900 flex items-center gap-2">
                            <Users className="w-6 h-6 text-primary-600" />
                            Manage Supervisors
                        </h1>
                        <p className="text-secondary-500 mt-1 text-sm">
                            View and manage clinician–supervisor assignments within your organisation.
                        </p>
                    </div>
                    <button
                        onClick={() => setModalOpen(true)}
                        className="flex items-center gap-2 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-semibold transition-colors flex-shrink-0 shadow-sm"
                    >
                        <UserPlus className="w-4 h-4" />
                        Assign Clinician
                    </button>
                </div>

                {error && (
                    <div className="flex items-center gap-2 bg-red-50 border border-red-100 text-red-700 rounded-lg px-4 py-3 text-sm">
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />{error}
                    </div>
                )}

                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-2.5 w-4 h-4 text-secondary-400 pointer-events-none" />
                        <input
                            type="text"
                            placeholder="Filter by supervisor..."
                            value={filterSupervisor}
                            onChange={e => setFilterSupervisor(e.target.value)}
                            className="w-full pl-9 pr-3 py-2.5 border border-secondary-200 rounded-lg text-sm text-secondary-800 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
                        />
                    </div>
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-2.5 w-4 h-4 text-secondary-400 pointer-events-none" />
                        <input
                            type="text"
                            placeholder="Filter by clinician..."
                            value={filterReportee}
                            onChange={e => setFilterReportee(e.target.value)}
                            className="w-full pl-9 pr-3 py-2.5 border border-secondary-200 rounded-lg text-sm text-secondary-800 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
                        />
                    </div>
                </div>

                {/* Table View Container */}
                <div className="bg-white rounded-xl border border-secondary-100 shadow-sm overflow-hidden">
                    {/* Desktop View */}
                    <div className="hidden md:block overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-secondary-100 bg-secondary-50/60">
                                    <th className="text-left px-6 py-3 text-xs font-semibold text-secondary-500 uppercase tracking-wide">Supervisor</th>
                                    <th className="text-left px-6 py-3 text-xs font-semibold text-secondary-500 uppercase tracking-wide">Junior Clinician</th>
                                    <th className="text-left px-6 py-3 text-xs font-semibold text-secondary-500 uppercase tracking-wide hidden sm:table-cell">Assigned</th>
                                    <th className="px-6 py-3" />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-secondary-50">
                                {loading ? (
                                    <tr>
                                        <td colSpan={4} className="px-6 py-12 text-center text-secondary-400 text-sm animate-pulse">Loading...</td>
                                    </tr>
                                ) : filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} className="px-6 py-12 text-center text-secondary-400 text-sm">
                                            {rows.length === 0 ? 'No assignments yet. Click "Assign Clinician" to get started.' : 'No results match your filters.'}
                                        </td>
                                    </tr>
                                ) : (
                                    filtered.map((row, i) => (
                                        <tr key={`${row.supervisorId}-${row.reporteeId}-${i}`} className="hover:bg-secondary-50/50 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <Avatar name={row.supervisorName} colorClass="bg-primary-100 text-primary-700" />
                                                    <div>
                                                        <p className="font-semibold text-secondary-800">{row.supervisorName}</p>
                                                        <p className="text-xs text-secondary-400">{row.supervisorEmail}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <Avatar name={row.reporteeName} colorClass="bg-violet-100 text-violet-700" />
                                                    <div>
                                                        <p className="font-semibold text-secondary-800">{row.reporteeName}</p>
                                                        <p className="text-xs text-secondary-400">{row.reporteeEmail}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-xs text-secondary-400 hidden sm:table-cell">
                                                {row.assignedAt ? new Date(row.assignedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <button
                                                    onClick={() => handleUnassign(row.supervisorId, row.reporteeId)}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 border border-red-100 hover:border-red-200 rounded-lg transition-colors"
                                                    title="Remove assignment"
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                    Remove
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile View */}
                    <div className="md:hidden divide-y divide-secondary-100 bg-secondary-50/30">
                        {loading ? (
                            <div className="px-6 py-12 text-center text-secondary-400 text-sm animate-pulse">Loading...</div>
                        ) : filtered.length === 0 ? (
                            <div className="px-6 py-12 text-center text-secondary-400 text-sm">
                                {rows.length === 0 ? 'No assignments yet. Click "Assign Clinician" to get started.' : 'No results match your filters.'}
                            </div>
                        ) : (
                            groupedBySupMobile.map(group => (
                                <div key={`mob-sup-${group.supervisorId}`} className="bg-white">
                                    <div 
                                        className="p-4 flex items-center justify-between hover:bg-secondary-50/50 cursor-pointer transition-colors"
                                        onClick={() => toggleMobileSup(group.supervisorId)}
                                    >
                                        <div className="flex items-center gap-3">
                                            <Avatar name={group.supervisorName} colorClass="bg-primary-100 text-primary-700" />
                                            <div>
                                                <p className="font-semibold text-secondary-800">{group.supervisorName}</p>
                                                <p className="text-xs text-secondary-500">{group.reportees.length} clinician{group.reportees.length !== 1 ? 's' : ''}</p>
                                            </div>
                                        </div>
                                        <ChevronDown className={`w-5 h-5 text-secondary-400 transition-transform ${expandedMobileSups[group.supervisorId] ? 'rotate-180' : ''}`} />
                                    </div>
                                    
                                    {expandedMobileSups[group.supervisorId] && (
                                        <div className="bg-secondary-50/50 border-t border-secondary-100 divide-y divide-secondary-100/50">
                                            {group.reportees.map((row, i) => (
                                                <div key={`${row.supervisorId}-${row.reporteeId}-${i}-rep`} className="p-4 pl-6">
                                                    <div className="flex items-center gap-3 mb-3">
                                                        <Avatar name={row.reporteeName} colorClass="bg-violet-100 text-violet-700" />
                                                        <div>
                                                            <p className="text-sm font-semibold text-secondary-800">{row.reporteeName}</p>
                                                            <p className="text-xs text-secondary-400">{row.reporteeEmail}</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center justify-between mt-2 pt-3 border-t border-secondary-100/50">
                                                        <div className="text-xs text-secondary-400">
                                                            <span className="font-semibold text-secondary-500 mr-1">Assigned:</span>
                                                            {row.assignedAt ? new Date(row.assignedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                                                        </div>
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); handleUnassign(row.supervisorId, row.reporteeId); }}
                                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 border border-red-100 hover:border-red-200 rounded-lg transition-colors"
                                                            title="Remove assignment"
                                                        >
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                            Remove
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>

                    {!loading && filtered.length > 0 && (
                        <div className="px-6 py-3 border-t border-secondary-100 bg-secondary-50/40 text-xs text-secondary-400">
                            {filtered.length} assignment{filtered.length !== 1 ? 's' : ''}{rows.length !== filtered.length ? ` (filtered from ${rows.length})` : ''}
                        </div>
                    )}
                </div>
            </div>

            <AssignModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                supervisors={supervisors}
                clinicians={clinicians}
                onAssigned={handleAssigned}
            />
        </DashboardLayout>
    );
}
